import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Plus, Edit2, Trash2, X, Check, Tag, AlertCircle, Shield,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ADMIN_EMAILS = [
  'diane.shawe@yahoo.com',
  'admin@mydirectdebits.com',
  'support@qualiquiz.app',
  'info@virtualautochat.uk',
];

const EMPTY_FORM = {
  code: '',
  discount: 0,
  applies_to: 'yearly',
  full_access: false,
  active: true,
  label: '',
};

export default function AdminPromoPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null = not open, 'new' = add, or code being edited
  const [form, setForm] = useState(EMPTY_FORM);

  const isAdmin =
    user && (ADMIN_EMAILS.includes((user.email || '').toLowerCase()) || user.is_admin);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/promo-codes`);
      setCodes(res.data);
    } catch (err) {
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/dashboard');
      return;
    }
    if (isAdmin) fetchCodes();
  }, [authLoading, isAdmin, navigate, fetchCodes]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing('new');
  };

  const openEdit = (c) => {
    setForm({
      code: c.code,
      discount: c.discount,
      applies_to: c.applies_to,
      full_access: c.full_access,
      active: c.active,
      label: c.label || '',
    });
    setEditing(c.code);
  };

  const closeModal = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    const code = form.code.trim().toUpperCase();
    if (!code) { toast.error('Code is required'); return; }
    if (form.discount < 0) { toast.error('Discount must be 0 or greater'); return; }
    setSaving(true);
    try {
      const payload = {
        code,
        discount: Number(form.discount),
        applies_to: form.applies_to,
        full_access: !!form.full_access,
        active: !!form.active,
        label: form.label.trim(),
      };
      if (editing === 'new') {
        await axios.post(`${API_URL}/api/admin/promo-codes`, payload);
        toast.success(`Added ${code}`);
      } else {
        await axios.put(`${API_URL}/api/admin/promo-codes/${editing}`, payload);
        toast.success(`Updated ${code}`);
      }
      await fetchCodes();
      closeModal();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.put(`${API_URL}/api/admin/promo-codes/${c.code}`, { active: !c.active });
      toast.success(`${c.code} is now ${!c.active ? 'active' : 'paused'}`);
      await fetchCodes();
    } catch (err) {
      toast.error('Toggle failed');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete promo code "${c.code}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/promo-codes/${c.code}`);
      toast.success(`Deleted ${c.code}`);
      await fetchCodes();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const computeFinal = (discount, appliesTo) => {
    const plans = { monthly: 5, yearly: 35, trial: 3 };
    const original = plans[appliesTo] || 0;
    return Math.max(0, original - Number(discount || 0));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-page">
      <header className="glass-header px-4 py-4 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin" className="text-text-secondary hover:text-primary transition-colors" data-testid="back-to-admin">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-dark text-lg sm:text-xl truncate">Promo Codes</h1>
              <p className="text-xs text-text-light">Manage discount codes for Premium plans</p>
            </div>
          </div>
          <button
            onClick={openNew}
            data-testid="add-promo-btn"
            className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Code</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="card-default overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="promo-codes-table">
              <thead className="bg-primary-light/40">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-dark">Code</th>
                  <th className="text-left px-4 py-3 font-semibold text-dark">Discount</th>
                  <th className="text-left px-4 py-3 font-semibold text-dark hidden md:table-cell">Plan</th>
                  <th className="text-left px-4 py-3 font-semibold text-dark hidden md:table-cell">Final Price</th>
                  <th className="text-left px-4 py-3 font-semibold text-dark hidden lg:table-cell">Label</th>
                  <th className="text-center px-4 py-3 font-semibold text-dark">Uses</th>
                  <th className="text-center px-4 py-3 font-semibold text-dark">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-dark">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {codes.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-text-light">
                      <Tag className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      No promo codes yet. Click "Add Code" to create one.
                    </td>
                  </tr>
                ) : (
                  codes.map((c) => (
                    <tr key={c.code} className="hover:bg-primary-light/20 transition-colors" data-testid={`promo-row-${c.code}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-dark">{c.code}</span>
                          {c.full_access && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gold-light text-gold font-semibold flex items-center gap-1">
                              <Shield className="w-3 h-3" /> VIP
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-dark">£{c.discount.toFixed(2)} off</td>
                      <td className="px-4 py-3 capitalize text-text-secondary hidden md:table-cell">{c.applies_to}</td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="font-semibold text-primary">£{computeFinal(c.discount, c.applies_to).toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3 text-text-light hidden lg:table-cell">{c.label || '—'}</td>
                      <td className="px-4 py-3 text-center font-semibold text-dark">{c.usage_count}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleActive(c)}
                          data-testid={`toggle-active-${c.code}`}
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                            c.active
                              ? 'bg-primary text-white hover:bg-primary-hover'
                              : 'bg-border text-text-secondary hover:bg-text-light hover:text-white'
                          }`}
                        >
                          {c.active ? 'Active' : 'Paused'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => openEdit(c)}
                            data-testid={`edit-${c.code}`}
                            className="p-2 rounded-lg hover:bg-primary-light text-primary transition-colors"
                            aria-label={`Edit ${c.code}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            data-testid={`delete-${c.code}`}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                            aria-label={`Delete ${c.code}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 rounded-xl bg-primary-light/40 border border-primary/20 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-text-secondary leading-relaxed">
            <p className="mb-1"><strong className="text-dark">Discount</strong> is the £ amount subtracted from the plan price.</p>
            <p className="mb-1"><strong className="text-dark">Full access</strong> grants the plan instantly (no Stripe) when the code reduces the price to £0 — use sparingly and only for VIPs.</p>
            <p><strong className="text-dark">Pause</strong> (vs Delete) keeps the code but stops it from being redeemable — useful for seasonal campaigns.</p>
          </div>
        </div>
      </main>

      {/* Edit / New modal */}
      {editing && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="promo-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="font-heading font-bold text-dark text-lg">
                {editing === 'new' ? 'Add Promo Code' : `Edit ${editing}`}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-1 rounded-lg text-text-light hover:text-dark hover:bg-border/40 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-text-secondary uppercase mb-1">Code</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  disabled={editing !== 'new'}
                  placeholder="e.g. SPRING25"
                  data-testid="input-code"
                  className="w-full px-4 py-2.5 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none uppercase disabled:bg-border/30 disabled:text-text-light"
                />
                {editing !== 'new' && <p className="text-xs text-text-light mt-1">Code cannot be renamed. Delete & recreate if needed.</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-text-secondary uppercase mb-1">Discount (£)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.discount}
                    onChange={(e) => setForm({ ...form, discount: e.target.value })}
                    data-testid="input-discount"
                    className="w-full px-4 py-2.5 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-text-secondary uppercase mb-1">Applies To</label>
                  <select
                    value={form.applies_to}
                    onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
                    data-testid="input-applies-to"
                    className="w-full px-4 py-2.5 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none capitalize"
                  >
                    <option value="yearly">Yearly (£35)</option>
                    <option value="monthly">Monthly (£5)</option>
                    <option value="trial">Trial (£3)</option>
                  </select>
                </div>
              </div>

              <div className="bg-primary-light/30 rounded-xl px-4 py-3">
                <p className="text-sm text-text-secondary">
                  Customer pays: <strong className="text-primary text-lg">£{computeFinal(form.discount, form.applies_to).toFixed(2)}</strong>
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-secondary uppercase mb-1">Internal Label (optional)</label>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g. Spring campaign 2026"
                  data-testid="input-label"
                  className="w-full px-4 py-2.5 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="input-active"
                  className="w-5 h-5 rounded border-border text-primary focus:ring-primary/20"
                />
                <span className="text-sm text-dark">Active (customers can redeem this code)</span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.full_access}
                  onChange={(e) => setForm({ ...form, full_access: e.target.checked })}
                  data-testid="input-full-access"
                  className="w-5 h-5 rounded border-border text-primary focus:ring-primary/20 mt-0.5"
                />
                <span className="text-sm text-dark">
                  Grant full access (VIP)
                  <span className="block text-xs text-text-light mt-0.5">
                    Skips Stripe entirely when price hits £0. Only enable for trusted VIPs — the code gives a free plan.
                  </span>
                </span>
              </label>
            </div>

            <div className="flex gap-3 p-5 border-t border-border">
              <button
                type="button"
                onClick={closeModal}
                className="flex-1 px-4 py-2.5 rounded-xl border border-border text-text-secondary hover:bg-border/30 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-promo-btn"
                className="flex-1 btn-primary px-4 py-2.5 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {editing === 'new' ? 'Add Code' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
